import {
  AttendeeConnection,
  AttendeeConnectionSearchableStatus,
  AttendeeConnectionPaginatedResults,
  AttendeeConnectionPendingCount
} from '@graphql/gql/graphql';
import { AttendeeConnections } from '@state/index';
import { ApolloQueryResult, InMemoryCache } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { PENDING_CONNECTIONS_PAGE_SIZE } from '@components/connections/PendingConnections';
import { CacheIdentifier } from './types';

const LOG = LoggerFactory.create('Attendee Connections Cache');

export const cacheIdentityConnection = (id): CacheIdentifier => ({
  id,
  __typename: 'AttendeeConnection'
});

export function getExistingAttendeeConnection(attendeeId: string, cache: InMemoryCache): AttendeeConnection {
  return cache.readFragment({
    id: cache.identify(cacheIdentityConnection(attendeeId)),
    fragment: AttendeeConnections.Queries.AttendeeConnectionFragment
  });
}

function getExistingConnectionsList(
  cache: InMemoryCache,
  status: AttendeeConnectionSearchableStatus
): AttendeeConnectionPaginatedResults | undefined {
  const result: {
    searchAttendeeConnections: AttendeeConnectionPaginatedResults;
  } = cache.readQuery({
    query: AttendeeConnections.Queries.SEARCH_ATTENDEE_CONNECTIONS,
    variables: {
      criteria: {
        status
      }
    }
  });

  return result?.searchAttendeeConnections;
}

/**
 * Called whenever a connection should no longer be PENDING (aka declined, accepted)
 */
export function removeAttendeeFromPendingConnections(attendeeId: string, cache: InMemoryCache): void {
  const attendeeConnection = getExistingAttendeeConnection(attendeeId, cache);

  if (attendeeConnection) {
    const attendeeConnectionList = getExistingConnectionsList(cache, AttendeeConnectionSearchableStatus.Pending);

    if (!attendeeConnectionList) {
      return;
    }

    const { paging, data } = attendeeConnectionList;

    const success = cache.writeQuery({
      query: AttendeeConnections.Queries.SEARCH_ATTENDEE_CONNECTIONS,
      data: {
        searchAttendeeConnections: {
          paging,
          data
        }
      },
      variables: {
        criteria: {
          status: AttendeeConnectionSearchableStatus.Pending
        },
        removeConnection: attendeeConnection
      }
    });

    if (!success) {
      LOG.warn(`Failed to remove attendee ${attendeeId} from pending connection list cached results`);
    }
  }
}

/**
 * Called when an existing connection is removed
 */
export function removeAttendeeFromConnections(attendeeId: string, cache: InMemoryCache): void {
  const attendeeConnection = getExistingAttendeeConnection(attendeeId, cache);

  if (attendeeConnection) {
    const attendeeConnectionList = getExistingConnectionsList(cache, AttendeeConnectionSearchableStatus.Connected);

    if (!attendeeConnectionList) {
      return;
    }

    const { paging, data } = attendeeConnectionList;

    const success = cache.writeQuery({
      query: AttendeeConnections.Queries.SEARCH_ATTENDEE_CONNECTIONS,
      data: {
        searchAttendeeConnections: {
          paging,
          data
        }
      },
      variables: {
        criteria: {
          status: AttendeeConnectionSearchableStatus.Connected
        },
        removeConnection: attendeeConnection
      }
    });

    if (!success) {
      LOG.warn(`Failed to remove attendee ${attendeeId} from connection list cached results`);
    }
  }
}

/**
 * Called when an existing connection moves status to Connected
 */
export function addAttendeeToConnections(attendeeId: string, cache: InMemoryCache): void {
  const attendeeConnection = getExistingAttendeeConnection(attendeeId, cache);

  if (attendeeConnection) {
    const attendeeConnectionList = getExistingConnectionsList(cache, AttendeeConnectionSearchableStatus.Connected);

    if (!attendeeConnectionList) {
      return;
    }

    const { paging, data } = attendeeConnectionList;

    const success = cache.writeQuery({
      query: AttendeeConnections.Queries.SEARCH_ATTENDEE_CONNECTIONS,
      data: {
        searchAttendeeConnections: {
          paging,
          data
        }
      },
      variables: {
        criteria: {
          status: AttendeeConnectionSearchableStatus.Connected
        },
        newConnection: attendeeConnection
      }
    });

    if (!success) {
      LOG.warn(`Failed to add attendee ${attendeeId} to connection list cached results`);
    }
  }
}

function getExistingPendingConnectionCount(cache: InMemoryCache): number {
  const result: {
    getPendingAttendeeConnectionCount: AttendeeConnectionPendingCount;
  } = cache.readQuery({
    query: AttendeeConnections.Queries.GET_PENDING_ATTENDEE_CONNECTION_COUNT
  });

  return result?.getPendingAttendeeConnectionCount?.pendingCount || 0;
}

export function decrementPendingConnectionCount(cache: InMemoryCache): void {
  const pendingCount = getExistingPendingConnectionCount(cache);

  if (pendingCount > 0) {
    const success = cache.writeQuery({
      query: AttendeeConnections.Queries.GET_PENDING_ATTENDEE_CONNECTION_COUNT,
      data: {
        getPendingAttendeeConnectionCount: {
          __typename: 'AttendeeConnectionPendingCount',
          pendingCount: pendingCount - 1
        }
      }
    });

    if (!success) {
      LOG.warn('Failed to decrement pending connection count');
    }
  }
}

/**
 * Fetch the next page of pending connection data when the known pending connection count drops below a threshold,
 * where the action that may have caused this is from an unrelated component.
 */
export function onQueryUpdated(
  watchedQuery,
  cache: InMemoryCache
): boolean | Promise<ApolloQueryResult<AttendeeConnectionPaginatedResults>> {
  if (
    watchedQuery.queryName === 'SEARCH_ATTENDEE_CONNECTIONS' &&
    watchedQuery.options.variables.criteria.status === AttendeeConnectionSearchableStatus.Pending &&
    watchedQuery.options.variables.criteria.limit === PENDING_CONNECTIONS_PAGE_SIZE
  ) {
    const lastResult = watchedQuery.getCurrentResult().data.searchAttendeeConnections;

    // length after cache manipulations performed in mutation update method
    const remainingPendingConnectionListLengthAfterAction =
      getExistingConnectionsList(cache, AttendeeConnectionSearchableStatus.Pending)?.data?.length || 0;

    // fetch next page of data if available when cached entries less than specified limit
    if (
      lastResult.paging.nextToken &&
      remainingPendingConnectionListLengthAfterAction < watchedQuery.options.variables.criteria.limit
    ) {
      return watchedQuery.fetchMore({
        variables: {
          criteria: {
            ...watchedQuery.options.variables.criteria,
            token: lastResult.paging.nextToken
          }
        }
      });
    }
  }

  // Flush in memory cache updates to other watched queries
  return undefined;
}
